// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';

import { BaseProps } from '../Base';
import { StyledButton } from './Styled';

export interface ButtonProps
  extends Omit<HTMLAttributes<HTMLButtonElement>, 'css' | 'type'>,
    BaseProps {
  /** The icon element to be shown in the button. */
  icon?: JSX.Element;
  /** The text of the button. */
  label: string | ReactNode;
  /** The variant of button. */
  variant?: ButtonVariant;
  /** Whether or not the button is selected. */
  selected?: boolean;
  /** Defines the size of the icon in the button, it defaults to `sm`. */
  iconSize?: IconSize;
  /** Whether or not the button is disabled. **/
  disabled?: boolean;
  /** Whether or not the button is outline. */
  outline?: boolean;
  /** ボタンのサイズを定義する。デフォルトは`lg`。 */
  size?: ButtonSize;
  /** アイコンを文字列の後に置くかどうか。*/
  iconAtEnd?: boolean;
  /** SP表示にアイコンのみ表示するかどうか。*/
  onlyIconAtSp?: boolean;
  /** ボタンのtype属性。*/
  type?: 'submit' | 'button' | 'reset';
}

export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';
export type IconSize = 'sm' | 'md' | 'lg' | 'auto';
export type IconPosition = 'start' | 'end';
export type ButtonVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'info'
  | 'error'
  | 'icon';

export const Button = forwardRef(
  (props: ButtonProps, ref: React.Ref<HTMLButtonElement>) => (
    <StyledButton
      {...props}
      className={props.className || ''}
      as={props.tag}
      ref={ref}
      aria-label={props.label}
      data-testid="button"
      disabled={props.disabled}
    >
      {props.icon && !props.iconAtEnd && (
        <span className="ch-icon" data-testid="button-icon">
          {props.icon}
        </span>
      )}
      <span className="ch-label" data-testid="button-label">
        {props.label}
      </span>
      {props.icon && props.iconAtEnd && (
        <span className="ch-icon" data-testid="button-icon">
          {props.icon}
        </span>
      )}
    </StyledButton>
  )
);
Button.displayName = 'Button';

export default Button;
